import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/store';
import { Helmet } from 'react-helmet-async';
import Header from '../../../shared/Header/Header';
import SectionHeader from '../../../layouts/showcaseLayouts/Section/SectionHeader/SectionHeader';
import BackButton from '../../../shared/backButton/BackButton';
import Footer from '../../../shared/Footer/Footer';
import useIsLTE650 from '../../../../hooks/useIsLTE650';
import './SiteMapPage.scss';

type CatInfo = { slug: string; name: string };

function toTitleCase(s: string) {
    const spaced = s.replace(/[-_]+/g, ' ').trim();
    if (!spaced) return '';
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function SiteMapPage() {
    const isMobile = useIsLTE650(650);
    const { products } = useSelector((s: RootState) => s.product);

    const origin = (process.env.REACT_APP_CANONICAL_ORIGIN || 'https://s-gabai.ru').replace(/\/$/, '');

    const categories: CatInfo[] = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of (products as any[]) || []) {
            const cat = (p?.category ?? p?.section ?? p?.categoryId ?? p?.sectionId) as any;
            if (!cat) continue;

            const slugRaw = String(cat?.url ?? cat?.slug ?? cat?.code ?? '')
                .trim()
                .toLowerCase();
            if (!slugRaw) continue;

            const nameRaw = String(cat?.name ?? cat?.title ?? '').trim();
            const name = nameRaw || toTitleCase(slugRaw);

            if (!map.has(slugRaw)) map.set(slugRaw, name);
        }

        return Array.from(map.entries())
            .map(([slug, name]) => ({ slug, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    }, [products]);

    const canonicalHref = new URL('/site-map/', origin + '/').toString();

    return (
        <>
            <Helmet>
                <title>Карта сайта — S-Gabai</title>
                <meta
                    name="description"
                    content="HTML-карта сайта S-Gabai: главная, каталог, категории, информация для покупателей, доставка и оплата, контакты, политика и условия."
                />
                <link rel="canonical" href={canonicalHref} />
            </Helmet>
            <Header isMobile={isMobile} />
            <main className="sg-sitemap">
                <div className="sg-sitemap_headerWrapper">
                    <BackButton absolute />
                    <SectionHeader title="Карта сайта" />
                </div>

                <nav aria-label="Карта сайта" role="navigation" className="sg-sitemap__grid">
                    <section className="sg-sitemap__card">
                        <h2 className="sg-sitemap__subtitle">Основные разделы</h2>
                        <ul className="sg-sitemap__list">
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/`}>
                                    Главная
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/catalog/`}>
                                    Каталог
                                </a>
                                {categories.length > 0 && (
                                    <ul className="sg-sitemap__sublist">
                                        {categories.map((c) => (
                                            <li className="sg-sitemap__sublist-item" key={c.slug}>
                                                <a className="sg-sitemap__link" href={`${origin}/catalog/${encodeURIComponent(c.slug)}/`}>
                                                    {c.name}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/about-company/`}>
                                    О компании
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/hotel-products/`}>
                                    Продукция для гостиниц
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/contacts/`}>
                                    Контакты
                                </a>
                            </li>
                        </ul>
                    </section>

                    <section className="sg-sitemap__card">
                        <h2 className="sg-sitemap__subtitle">Покупателям и условия</h2>
                        <ul className="sg-sitemap__list">
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/customers/`}>
                                    Покупателям
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/delivery-payment/`}>
                                    Доставка и оплата
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/customers?section=return`}>
                                    Возврат
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/customers?section=offer`}>
                                    Пользовательское соглашение
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/customers?section=policy`}>
                                    Политика персональных данных
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/loyalty/`}>
                                    Бонусная программа
                                </a>
                            </li>
                        </ul>
                    </section>

                    {/* <section className="sg-sitemap__card">
                        <h2 className="sg-sitemap__subtitle">Коллекции</h2>
                        <ul className="sg-sitemap__list">
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/luxury-art/`}>
                                    Золотой шелк
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/sleep-territory/`}>
                                    Территория сна
                                </a>
                            </li>
                            <li className="sg-sitemap__list-item">
                                <a className="sg-sitemap__link" href={`${origin}/best-silk/`}>
                                    Лучший шёлк
                                </a>
                            </li>
                        </ul>
                    </section> */}
                </nav>
            </main>
            <Footer />
        </>
    );
}
